// offlineDB.js — Capa 1 del offline del chofer (reescrito robusto)
// Wrapper simple sobre IndexedDB. Autónomo.
// Stores: viajes_cache (keyPath 'id'), cola_sync (keyPath 'temp_id').
// Tolerante a fallos: si IndexedDB no está, degrada a "solo online".

const DB_NAME = 'combustible_offline';
const DB_VERSION = 2;
const STORE_VIAJES = 'viajes_cache';
const STORE_COLA = 'cola_sync';
const STORE_CLIENTES = 'clientes_cache';

const LOG = true; // diagnóstico temporal: poner en false cuando funcione
function log(...a) { if (LOG) console.log('[offlineDB]', ...a); }

function idbDisponible() {
  try { return typeof indexedDB !== 'undefined' && indexedDB !== null; }
  catch { return false; }
}

let _dbPromise = null;

function abrirDB() {
  if (!idbDisponible()) { log('IndexedDB NO disponible'); return Promise.resolve(null); }
  if (_dbPromise) return _dbPromise;

  _dbPromise = new Promise((resolve) => {
    let req;
    try {
      req = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (e) {
      log('open() lanzo excepcion', e);
      resolve(null);
      return;
    }
    req.onupgradeneeded = (e) => {
      const db = e.target.result;
      log('onupgradeneeded: creando stores');
      if (!db.objectStoreNames.contains(STORE_VIAJES)) {
        db.createObjectStore(STORE_VIAJES, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE_COLA)) {
        db.createObjectStore(STORE_COLA, { keyPath: 'temp_id' });
      }
      if (!db.objectStoreNames.contains(STORE_CLIENTES)) {
        db.createObjectStore(STORE_CLIENTES, { keyPath: 'id' });
      }
    };
    req.onsuccess = (e) => { log('DB abierta OK'); resolve(e.target.result); };
    req.onerror = (e) => { log('DB error', e.target.error); resolve(null); };
    req.onblocked = () => { log('DB blocked'); resolve(null); };
  });
  return _dbPromise;
}

// --- VIAJES CACHE ---

export async function guardarViajes(viajes) {
  const db = await abrirDB();
  if (!db) { log('guardarViajes: sin db'); return false; }
  if (!Array.isArray(viajes)) { log('guardarViajes: no es array'); return false; }
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_VIAJES, 'readwrite');
      const store = tx.objectStore(STORE_VIAJES);
      store.clear();
      for (const v of viajes) {
        if (v && v.id) store.put(v);
      }
      tx.oncomplete = () => { log('guardarViajes OK:', viajes.length, 'viajes'); resolve(true); };
      tx.onerror = () => { log('guardarViajes tx error', tx.error); resolve(false); };
      tx.onabort = () => { log('guardarViajes abort', tx.error); resolve(false); };
    } catch (e) { log('guardarViajes excepcion', e); resolve(false); }
  });
}

export async function leerViajes() {
  const db = await abrirDB();
  if (!db) { log('leerViajes: sin db'); return []; }
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_VIAJES, 'readonly');
      const store = tx.objectStore(STORE_VIAJES);
      const req = store.getAll();
      req.onsuccess = () => { log('leerViajes OK:', (req.result || []).length); resolve(req.result || []); };
      req.onerror = () => { log('leerViajes error'); resolve([]); };
    } catch (e) { log('leerViajes excepcion', e); resolve([]); }
  });
}


// --- CLIENTES CACHE ---

export async function guardarClientes(clientes) {
  const db = await abrirDB();
  if (!db || !Array.isArray(clientes)) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_CLIENTES, 'readwrite');
      const store = tx.objectStore(STORE_CLIENTES);
      store.clear();
      for (const c of clientes) { if (c && c.id) store.put(c); }
      tx.oncomplete = () => { log('guardarClientes OK:', clientes.length); resolve(true); };
      tx.onerror = () => resolve(false);
      tx.onabort = () => resolve(false);
    } catch { resolve(false); }
  });
}

export async function leerClientes() {
  const db = await abrirDB();
  if (!db) return [];
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_CLIENTES, 'readonly');
      const req = tx.objectStore(STORE_CLIENTES).getAll();
      req.onsuccess = () => { log('leerClientes OK:', (req.result||[]).length); resolve(req.result || []); };
      req.onerror = () => resolve([]);
    } catch { resolve([]); }
  });
}

// --- COLA DE SINCRONIZACION ---

export async function encolar(operacion) {
  const db = await abrirDB();
  if (!db) { log('encolar: sin db'); return null; }
  const item = {
    temp_id: 'temp_' + Date.now() + '_' + Math.random().toString(36).slice(2, 8),
    creado_en: new Date().toISOString(),
    intentos: 0,
    ...operacion,
  };
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_COLA, 'readwrite');
      tx.objectStore(STORE_COLA).put(item);
      tx.oncomplete = () => { log('encolar OK', item.temp_id); resolve(item); };
      tx.onerror = () => { log('encolar error', tx.error); resolve(null); };
      tx.onabort = () => { log('encolar abort', tx.error); resolve(null); };
    } catch (e) { log('encolar excepcion', e); resolve(null); }
  });
}

export async function leerCola() {
  const db = await abrirDB();
  if (!db) return [];
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_COLA, 'readonly');
      const req = tx.objectStore(STORE_COLA).getAll();
      req.onsuccess = () => {
        const items = req.result || [];
        items.sort((a, b) => (a.creado_en < b.creado_en ? -1 : 1));
        resolve(items);
      };
      req.onerror = () => resolve([]);
    } catch { resolve([]); }
  });
}

export async function borrarDeCola(temp_id) {
  const db = await abrirDB();
  if (!db) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_COLA, 'readwrite');
      tx.objectStore(STORE_COLA).delete(temp_id);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    } catch { resolve(false); }
  });
}

export async function actualizarEnCola(item) {
  const db = await abrirDB();
  if (!db || !item || !item.temp_id) return false;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_COLA, 'readwrite');
      tx.objectStore(STORE_COLA).put(item);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    } catch { resolve(false); }
  });
}

export async function contarCola() {
  const db = await abrirDB();
  if (!db) return 0;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE_COLA, 'readonly');
      const req = tx.objectStore(STORE_COLA).count();
      req.onsuccess = () => resolve(req.result || 0);
      req.onerror = () => resolve(0);
    } catch { resolve(0); }
  });
}

export function offlineDisponible() {
  return idbDisponible();
}

export default {
  guardarViajes, leerViajes,
  guardarClientes, leerClientes,
  encolar, leerCola, borrarDeCola, actualizarEnCola, contarCola,
  offlineDisponible,
};
